import { Component, computed, inject } from '@angular/core';
import { injectDispatch } from '@ngrx/signals/events';
import { FormsModule } from '@angular/forms';
import { PortsStore } from '@navstation-portal/ports/data-access/store';
import { StockTankFormState } from '@navstation-portal/reporting/data-model/vessel-report';
import { portsToSelectOptions } from '@navstation-portal/ports/util/ports';
import {
  AbstractStoreConnectedSectionComponent,
  offshoreStockFormEvents,
  OffshoreStockFormStore,
} from '@navstation-portal/reporting/data-access/offshore-report';
import { TableColumn } from '../../../../shared/stock-table/stock-table.component';
import { InUseTanksComponent } from '../in-use-tanks/in-use-tanks.component';
import { NotInUseTanksComponent } from '../not-in-use-tanks/not-in-use-tanks.component';
import { FuelTypeTotalsComponent } from '../fuel-type-totals/fuel-type-totals.component';

@Component({
  selector: 're-tank-stock-table',
  templateUrl: './tank-stock-table.component.html',
  imports: [
    FormsModule,
    InUseTanksComponent,
    NotInUseTanksComponent,
    FuelTypeTotalsComponent,
  ],
})
export class TankStockTableComponent extends AbstractStoreConnectedSectionComponent {
  tabId = 'stock';
  sectionId = 'tanks';
  fieldId = 'tankRows';
  stockStore = inject(OffshoreStockFormStore);
  portStore = inject(PortsStore);
  events = injectDispatch(offshoreStockFormEvents);
  portOptions = computed(() => {
    return portsToSelectOptions(this.portStore.ports());
  });

  get columns(): TableColumn<StockTankFormState>[] {
    return [
      {
        header: 'Tank Name',
        field: 'tankName',
        type: 'rowTitle',
        title: 'fuel',
        subtitleField: 'tankCapacity',
      },
      {
        header: 'Stock (MT)',
        field: 'amount',
        type: 'number',
        minWidth: '70px',
        requiredFunction: (tank: StockTankFormState) => this.isInUseTank(tank),
      },
      {
        header: 'Sulphur (%)',
        field: 'sulphurAfterBunkering',
        type: 'number',
        minWidth: '70px',
        readOnly: true,
      },
      {
        header: 'LCV (kJ/kg)',
        field: 'lcv',
        type: 'number',
        minWidth: '110px',
        readOnly: true,
      },
      {
        header: 'Density (kg/m³)',
        field: 'density',
        type: 'number',
        minWidth: '100px',
        readOnly: true,
      },
      {
        header: 'Viscosity (cSt)',
        field: 'viscosity',
        type: 'number',
        minWidth: '90px',
        readOnly: true,
      },
      {
        header: 'Fuel Grade',
        field: 'fuelGrade',
        type: 'select',
        options: this.reportStore.availableFuelGradeOptions(),
        readOnly: true,
      },
      {
        header: 'Bunker Port',
        field: 'bunkerPort',
        type: 'autocomplete',
        options: this.portOptions(),
        bgColorField: 'groupColor',
        readOnly: true,
      },
      {
        header: 'Bunker Date',
        field: 'bunkerDate',
        type: 'datepicker',
        readOnly: true,
        minWidth: '150px',
      },
      {
        header: 'In Use',
        field: 'inUse',
        type: 'checkbox',
        minWidth: '40px',
        readOnly: true,
        hideFunction: (tank: StockTankFormState) =>
          this.hideTankInUseCheckbox(tank),
      },
    ];
  }

  hideTankInUseCheckbox(tank: StockTankFormState) {
    return !!tank.tankName.value?.match(/SERV|SETT|OVERFLOW/i);
  }

  isInUseTank(tank: StockTankFormState) {
    return (
      tank.inUse.value || !!tank.tankName.value?.match(/SERV|SETT|OVERFLOW/i)
    );
  }
}
